import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Shield, RefreshCw, ChevronDown, ChevronRight, Save } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_LABELS = {
  acces: "Droit d'accès",
  rectification: 'Rectification',
  effacement: "Effacement (oubli)",
  portabilite: 'Portabilité',
  opposition: 'Opposition',
  limitation: 'Limitation',
};

const STATUS_LABELS = {
  nouveau: 'Nouveau',
  en_cours: 'En cours',
  traite: 'Traité',
  rejete: 'Rejeté',
};

const STATUS_COLOR = {
  nouveau: 'bg-blue-500/20 text-blue-300 border-blue-500/50',
  en_cours: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/50',
  traite: 'bg-green-500/20 text-green-300 border-green-500/50',
  rejete: 'bg-gray-500/20 text-gray-300 border-gray-500/50',
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Intl.DateTimeFormat('fr-FR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(iso)); } catch { return iso; }
};

const RgpdRequestsAdmin = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/rgpd/requests`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(data);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateRequest = async (id, status, adminNote) => {
    try {
      await axios.patch(`${API_URL}/api/admin/rgpd/requests/${id}`,
        { status, admin_note: adminNote },
        { headers: { Authorization: `Bearer ${token}` } });
      fetchData();
    } catch (err) {
      alert(`Erreur : ${err?.response?.data?.detail || err.message}`);
    }
  };

  const requests = data?.requests || [];
  const stats = data?.stats || {};

  return (
    <div className="bg-[#302f2c] border border-[#3f4816] rounded-xl overflow-hidden mb-6" data-testid="rgpd-admin">
      <div className="p-4 border-b border-[#3f4816] flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Shield className="text-[#d9fb06]" size={20} />
          <h2 className="text-lg font-bold text-[#dfddd6]">
            Demandes RGPD <span className="text-[#888680]">({stats.total || 0})</span>
          </h2>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 text-xs border border-[#3f4816] text-[#dfddd6] px-3 py-2 rounded-lg hover:border-[#d9fb06] hover:text-[#d9fb06]"
          data-testid="rgpd-refresh-btn"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Actualiser
        </button>
      </div>

      <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3 border-b border-[#3f4816]">
        {['nouveau', 'en_cours', 'traite', 'rejete'].map((k) => (
          <div key={k} className="bg-[#1a1c1b] border border-[#3f4816] rounded-lg p-3">
            <p className="text-xs text-[#888680] uppercase font-bold">{STATUS_LABELS[k]}</p>
            <p className={`mt-1 text-lg font-black ${k === 'nouveau' && stats[k] ? 'text-[#d9fb06]' : 'text-[#dfddd6]'}`}>{stats[k] || 0}</p>
          </div>
        ))}
      </div>

      <div className="max-h-[500px] overflow-y-auto">
        {loading && <div className="p-8 text-center text-[#888680]"><Loader2 className="animate-spin inline mr-2" size={16} /> Chargement…</div>}
        {error && <div className="p-6 text-red-400 text-sm">{error}</div>}
        {!loading && !error && requests.length === 0 && (
          <div className="p-8 text-center text-[#888680]" data-testid="rgpd-empty">Aucune demande RGPD reçue.</div>
        )}
        {!loading && !error && requests.map((r) => {
          const isOpen = expandedId === r.id;
          return (
            <div key={r.id} className="border-b border-[#3f4816]" data-testid={`rgpd-row-${r.id}`}>
              <button
                onClick={() => setExpandedId(isOpen ? null : r.id)}
                className="w-full p-4 flex flex-wrap items-center justify-between gap-3 hover:bg-[#1a1c1b]/40 text-left"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {isOpen ? <ChevronDown size={16} className="text-[#d9fb06]" /> : <ChevronRight size={16} className="text-[#888680]" />}
                  <div className="min-w-0">
                    <p className="text-[#dfddd6] font-semibold truncate">{r.nom} — {TYPE_LABELS[r.type_demande] || r.type_demande}</p>
                    <p className="text-xs text-[#888680] truncate">{r.email} · {formatDate(r.created_at)}</p>
                  </div>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full border ${STATUS_COLOR[r.status] || 'bg-gray-500/20 text-gray-300 border-gray-500/50'}`}>
                  {STATUS_LABELS[r.status] || r.status}
                </span>
              </button>
              {isOpen && <RequestDetail r={r} onUpdate={updateRequest} />}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const RequestDetail = ({ r, onUpdate }) => {
  const [status, setStatus] = useState(r.status);
  const [note, setNote] = useState(r.admin_note || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    await onUpdate(r.id, status, note);
    setSaving(false);
  };

  return (
    <div className="p-4 pt-0 pl-10 space-y-3 text-sm">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[#dfddd6]">
        <p><span className="text-[#888680] text-xs uppercase">ID</span> <span className="font-mono text-xs">{r.id.slice(0, 8)}</span></p>
        <p><span className="text-[#888680] text-xs uppercase">Reçue le</span> {formatDate(r.created_at)}</p>
      </div>
      {r.details && (
        <div>
          <p className="text-[#888680] text-xs uppercase mb-1">Détails du demandeur</p>
          <p className="text-[#dfddd6] whitespace-pre-wrap bg-[#1a1c1b] rounded p-3 text-sm">{r.details}</p>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-[#888680] uppercase font-bold block mb-1">Statut</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)}
            data-testid={`rgpd-status-select-${r.id}`}
            className="w-full bg-[#1a1c1b] border border-[#3f4816] rounded px-3 py-2 text-[#dfddd6]">
            {Object.entries(STATUS_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="text-xs text-[#888680] uppercase font-bold block mb-1">Note interne</label>
        <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000}
          className="w-full bg-[#1a1c1b] border border-[#3f4816] rounded px-3 py-2 text-[#dfddd6] resize-y" />
      </div>
      <button onClick={save} disabled={saving}
        data-testid={`rgpd-save-btn-${r.id}`}
        className="bg-[#d9fb06] text-[#1a1c1b] font-bold uppercase text-xs rounded-full px-4 py-2 disabled:opacity-50 flex items-center gap-2">
        {saving ? <Loader2 className="animate-spin" size={12} /> : <Save size={12} />} Enregistrer
      </button>
    </div>
  );
};

export default RgpdRequestsAdmin;
